import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { createCalendarNote, updateCalendarNote, fetchCalendarData, addCalendarNote } from '../../../store/calendarSlice';
import { searchTickets } from '../../../store/ticketSlice';
import OverflowTooltip from '../../../shared/OverflowTooltip';
import { CHARACTER_LIMITS, AUTOCOMPLETE_REASONS } from '../../../shared/appConstants';
import './AddNoteModal.scss';

const AddNoteModal = ({ open, onClose, projectId, dateRange, filters, note = null, noteDate = null, mode = 'create' }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { createNoteLoading, updateNoteLoading } = useSelector(state => state.calendar);
  const { dropdowns } = useSelector(state => state.tickets);
  
  const [noteText, setNoteText] = useState('');
  const [assignee, setAssignee] = useState('');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketSearchTerm, setTicketSearchTerm] = useState('');
  const [ticketOptions, setTicketOptions] = useState([]);
  const [ticketLoading, setTicketLoading] = useState(false);
  
  // Store initial values for comparison
  const [initialValues, setInitialValues] = useState({
    text: '',
    assigneeId: '',
    ticketId: null
  });

  useEffect(() => {
    if (open) {
      if (note) {
        const text = note.text || '';
        const assigneeId = note.assignee?.id || '';
        const ticket = note.linkedTicket ? {
          id: note.linkedTicket.id,
          ticket_number: note.linkedTicket.ticket_number,
          title: note.linkedTicket.title
        } : null;
        
        setNoteText(text);
        setAssignee(assigneeId);
        setSelectedTicket(ticket);
        setInitialValues({
          text,
          assigneeId,
          ticketId: ticket?.id || null
        });
      } else {
        setNoteText('');
        setAssignee('');
        setSelectedTicket(null);
        setInitialValues({
          text: '',
          assigneeId: '',
          ticketId: null
        });
      }
    }
  }, [open, note]);

  useEffect(() => {
    if (!open || !projectId) {
      setTicketOptions([]);
      return;
    }

    // Load initial tickets on modal open
    if (!ticketSearchTerm) {
      setTicketLoading(true);
      dispatch(searchTickets({ projectId, searchTerm: '' }))
        .unwrap()
        .then(result => setTicketOptions(result.slice(0, 10)))
        .catch(() => {})
        .finally(() => setTicketLoading(false));
      return;
    }

    const timer = setTimeout(async () => {
      setTicketLoading(true);
      try {
        const result = await dispatch(searchTickets({ projectId, searchTerm: ticketSearchTerm })).unwrap();
        setTicketOptions(result.slice(0, 10));
      } catch (error) {
        // Error handled silently
      } finally {
        setTicketLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [ticketSearchTerm, projectId, open, dispatch]);

  const hasChanges = () => {
    if (!note) return noteText.trim().length > 0; // For create mode, just check if text exists
    
    return (
      noteText.trim() !== initialValues.text ||
      assignee !== initialValues.assigneeId ||
      (selectedTicket?.id || null) !== initialValues.ticketId
    );
  };

  const handleSubmit = async () => {
    if (!noteText.trim()) return;

    try {
      if (note) {
        const updatePayload = { noteId: note.id };
        
        // Only include fields that have changed
        if (noteText.trim() !== note.text) {
          updatePayload.text = noteText.trim();
        }
        
        // For assignee: only send if changed, send null only if explicitly cleared
        const currentAssigneeId = note.assignee?.id || '';
        if (assignee !== currentAssigneeId) {
          updatePayload.assignee = assignee || null;
        }
        
        // For ticket: only send if changed, send null only if explicitly cleared
        const currentTicketId = note.linkedTicket?.id || null;
        if (selectedTicket?.id !== currentTicketId) {
          updatePayload.ticket = selectedTicket?.id || null;
        }
        
        await dispatch(updateCalendarNote(updatePayload)).unwrap();
        toast.success(t('calendar.noteUpdatedSuccess'));
      } else {
        // Use noteDate as-is (YYYY-MM-DD format) for DATE column
        const formattedNoteDate = noteDate || new Date().toISOString().split('T')[0];
        const selectedAssignee = dropdowns.assignees.find(assigneeOption => String(assigneeOption.id) === String(assignee));
        const createdNote = await dispatch(createCalendarNote({
          projectId,
          text: noteText.trim(),
          assignee: assignee || undefined,
          ticket: selectedTicket?.id || undefined,
          note_date: formattedNoteDate
        })).unwrap();

        if (createdNote?.id) {
          dispatch(addCalendarNote({
            date: createdNote?.note_date || formattedNoteDate,
            note: {
              id: createdNote.id,
              text: createdNote?.text || noteText.trim(),
              note_date: createdNote?.note_date || formattedNoteDate,
              assignee: createdNote?.assignee || selectedAssignee || null,
              ticket: createdNote?.ticket || (selectedTicket ? {
                id: selectedTicket.id,
                ticket_number: selectedTicket.ticket_number,
                title: selectedTicket.title
              } : null),
              created_at: createdNote?.created_at || new Date().toISOString()
            }
          }));
        }
        toast.success(t('calendar.noteCreatedSuccess'));
      }
      
      if (note && dateRange) {
        dispatch(fetchCalendarData({ 
          projectId, 
          fromDate: dateRange.fromDate, 
          toDate: dateRange.toDate, 
          filters 
        }));
      }
      
      handleClose();
    } catch (error) {
      toast.error(error || t(note ? 'calendar.failedToUpdateNote' : 'calendar.failedToCreateNote'));
    }
  };

  const handleClose = () => {
    setNoteText('');
    setAssignee('');
    setSelectedTicket(null);
    setTicketSearchTerm('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth className="add-note-modal">
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" fontWeight="600">
            {note ? t('calendar.editNote') : t('calendar.addNote')}
          </Typography>
          <IconButton size="small" onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box className="modal-content">
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder={t('calendar.enterNote')}
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            variant="outlined"
            inputProps={{ maxLength: CHARACTER_LIMITS.CALENDAR_NOTE }}
            helperText={`${noteText.length}/${CHARACTER_LIMITS.CALENDAR_NOTE} ${t('calendar.characters')}`}
          />
          <FormControl fullWidth>
            <Select
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) return <em className="select-placeholder">{t('calendar.selectAssignee')}</em>;
                const selectedAssignee = dropdowns.assignees.find(assigneeOption => assigneeOption.id === selected);
                return selectedAssignee?.name || '';
              }}
              MenuProps={{
                PaperProps: {
                  className: 'add-note-dropdown-menu'
                }
              }}
            >
              <MenuItem value="" className="none-menu-item">{t('calendar.none')}</MenuItem>
              {dropdowns.assignees.map((assigneeOption) => (
                <MenuItem key={assigneeOption.id} value={assigneeOption.id}>
                  <OverflowTooltip title={assigneeOption.name}>
                    {assigneeOption.name}
                  </OverflowTooltip>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <Autocomplete
              options={ticketOptions}
              value={selectedTicket}
              onChange={(event, newValue) => {
                setSelectedTicket(newValue);
                if (!newValue) {
                  setTicketSearchTerm('');
                }
              }}
              onInputChange={(event, newInputValue, reason) => {
                if (reason !== AUTOCOMPLETE_REASONS.RESET && reason !== AUTOCOMPLETE_REASONS.CLEAR) {
                  setTicketSearchTerm(newInputValue);
                }
              }}
              loading={ticketLoading}
              getOptionLabel={(option) => {
                if (!option) return '';
                return option.ticket_number && option.title 
                  ? `${option.ticket_number} - ${option.title}` 
                  : option.ticket_number || option.title || '';
              }}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.id}>
                  <OverflowTooltip title={`${option.ticket_number} - ${option.title}`}>
                    {`${option.ticket_number} - ${option.title}`}
                  </OverflowTooltip>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t('calendar.linkTicket')}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {ticketLoading ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              noOptionsText={ticketSearchTerm ? t('calendar.noTicketsFound') : t('calendar.startTypingToSearch')}
              className="note-autocomplete"
              slotProps={{
                paper: {
                  className: 'note-autocomplete'
                }
              }}
            />
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions className="dialog-actions">
        <Button
          fullWidth
          onClick={handleSubmit}
          variant="contained"
          disabled={!noteText.trim() || (note ? (!hasChanges() || updateNoteLoading) : createNoteLoading)}
          className="add-note-button"
        >
          {note ? (updateNoteLoading ? t('calendar.saving') : t('common.save')) : (createNoteLoading ? t('calendar.adding') : t('calendar.addNote'))}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddNoteModal;
